// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useEffect, useRef, useState } from "react"
import { BrowserMultiFormatReader } from "@zxing/library"
import type { PantryResponse } from "@bitebalance/contracts"
import { useI18n } from "@/i18n"
import { lookupBarcode, type BarcodeProduct } from "@/lib/barcodeLookup"
import { trackKitchenEvent } from "@/lib/kitchenAnalytics"
import { usePantryCapture } from "@/lib/usePantryCapture"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { CandidateConfirmSheet } from "@/components/pantry/CandidateConfirmSheet"
import { ManualEntrySheet } from "@/components/pantry/ManualEntrySheet"

/**
 * Barcode scan — the lean path (Kitchen redesign, 2026-09-14, ADR-011
 * reversal; web twin of mobile/src/app/scan-product.tsx). A scanned code
 * resolves against Open Food Facts, client-side (lib/barcodeLookup.ts), and
 * the resolved product feeds the EXISTING extraction/confirmation pipeline
 * — this dialog owns the camera and the lookup; everything after that is
 * the same CandidateConfirmSheet Kitchen already uses.
 *
 * @zxing/library (pure JS, no native permission-status API the way Expo's
 * useCameraPermissions has) is the browser decoder — browsers don't expose
 * a pre-flight permission check, so this simply attempts getUserMedia (via
 * zxing's decodeFromVideoDevice) and reads the rejection to tell "denied"
 * from "no camera" from "something else wrong," same distinctions the
 * mobile spec asks for.
 *
 * Every dead end the spec calls out has an explicit next step: camera
 * permission denied -> guidance + manual entry; barcode not in Open Food
 * Facts -> manual entry, nothing invented; a lookup or extraction error ->
 * try again or manual entry. Manual entry is the SAME ManualEntrySheet
 * Kitchen uses, rendered right here — never a bounce to another screen.
 */
type Phase =
  | "requesting"
  | "scanning"
  | "denied"
  | "unsupported"
  | "looking-up"
  | "not-found"
  | "lookup-error"
  | "manual"

export interface ScanProductDialogProps {
  open: boolean
  onClose: () => void
  onPersisted: (result: PantryResponse) => void
}

export function ScanProductDialog({ open, onClose, onPersisted }: ScanProductDialogProps) {
  const { t, locale } = useI18n()
  const [phase, setPhase] = useState<Phase>("requesting")
  const [product, setProduct] = useState<BarcodeProduct | null>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const readerRef = useRef<BrowserMultiFormatReader | null>(null)
  const locked = useRef(false)
  const startedTracked = useRef(false)

  const capture = usePantryCapture(result => {
    onPersisted(result)
  })

  async function handleBarcode(code: string) {
    if (locked.current) return
    locked.current = true
    setPhase("looking-up")
    const result = await lookupBarcode(code, locale)
    if (result.found) {
      trackKitchenEvent("barcode_scan_success")
      setProduct(result.product)
      const ok = await capture.runExtraction(result.product.sourceText)
      if (!ok) setPhase("lookup-error")
    } else {
      trackKitchenEvent("barcode_scan_failed")
      setPhase("not-found")
    }
  }

  useEffect(() => {
    if (!open) return
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setPhase("unsupported")
      return
    }
    const reader = new BrowserMultiFormatReader()
    readerRef.current = reader
    setPhase("requesting")
    locked.current = false
    reader
      .decodeFromVideoDevice(null, videoRef.current, result => {
        if (result && !startedTracked.current) {
          startedTracked.current = true
        }
        if (result) void handleBarcode(result.getText())
      })
      .then(() => {
        if (!startedTracked.current) {
          startedTracked.current = true
          trackKitchenEvent("barcode_scan_started")
        }
        setPhase(p => (p === "requesting" ? "scanning" : p))
      })
      .catch((err: unknown) => {
        const name = err instanceof Error ? err.name : ""
        setPhase(
          name === "NotAllowedError" || name === "PermissionDeniedError" ? "denied" : "unsupported"
        )
      })
    return () => {
      reader.reset()
      readerRef.current = null
      startedTracked.current = false
    }
    // Deliberately narrow dependency list: re-run only when the dialog opens, not on every render.
  }, [open])

  function resumeScanning() {
    locked.current = false
    setProduct(null)
    setPhase("scanning")
  }

  function handleManualSubmit(sourceText: string, expiryDate: string | null) {
    void capture.runExtraction(sourceText, expiryDate)
  }

  async function handleConfirm(candidates: Parameters<typeof capture.confirmCandidates>[0]) {
    const result = await capture.confirmCandidates(candidates)
    if (result.success) trackKitchenEvent("kitchen_item_added", { source: "scan" })
  }

  return (
    <Dialog open={open} onOpenChange={next => !next && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("views.pantry.scanButton")}</DialogTitle>
        </DialogHeader>

        {/* bg-green: the closest real design-tokens surface to mobile's dark "well" hero —
            there is no well/on-well token on web (checked packages/design-tokens directly
            before using it), so this reuses what the web palette actually has, rather than
            an arbitrary hex borrowed from a different platform's token set. */}
        <div className="relative flex min-h-[320px] flex-col items-center justify-center gap-4 overflow-hidden rounded-bb-lg bg-green">
          {/* the video element stays mounted across phases so zxing's stream never has to restart */}
          <video
            ref={videoRef}
            className={
              phase === "scanning" || phase === "looking-up" ? "size-full object-cover" : "hidden"
            }
            muted
            playsInline
          />

          {phase === "requesting" && <p className="text-sm text-paper/80">{t("common.loading")}</p>}

          {phase === "scanning" && (
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-end gap-3 pb-6">
              <div className="size-[60%] max-w-56 rounded-bb-lg border-2 border-cream/85" />
              <p className="text-sm text-paper/80">{t("views.pantry.scanInstructions")}</p>
            </div>
          )}

          {phase === "looking-up" && (
            <div className="rounded-bb-lg bg-paper p-5 text-center">
              <p className="text-sm text-ink-2">{t("common.loading")}</p>
            </div>
          )}

          {(phase === "denied" || phase === "unsupported") && (
            <div className="max-w-sm rounded-bb-lg bg-paper p-5 text-center">
              <p className="font-semibold text-ink">
                {t("views.pantry.scanPermissionDeniedTitle")}
              </p>
              <p className="mt-2 text-sm text-ink-2">
                {t("views.pantry.scanPermissionDeniedBody")}
              </p>
              <Button
                type="button"
                className="mt-4 min-h-10 w-full"
                onClick={() => setPhase("manual")}
              >
                {t("views.pantry.addManuallyButton")}
              </Button>
            </div>
          )}

          {phase === "not-found" && (
            <div className="max-w-sm rounded-bb-lg bg-paper p-5 text-center">
              <p className="font-semibold text-ink">{t("views.pantry.scanNotFoundTitle")}</p>
              <p className="mt-2 text-sm text-ink-2">{t("views.pantry.scanNotFoundBody")}</p>
              <div className="mt-4 flex flex-col gap-2">
                <Button type="button" className="min-h-10" onClick={() => setPhase("manual")}>
                  {t("views.pantry.addManuallyButton")}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="min-h-10"
                  onClick={resumeScanning}
                >
                  {t("views.pantry.scanTryAgain")}
                </Button>
              </div>
            </div>
          )}

          {phase === "lookup-error" && (
            <div className="max-w-sm rounded-bb-lg bg-paper p-5 text-center">
              <p className="text-sm text-ink-2">{t("views.pantry.scanErrorBody")}</p>
              <div className="mt-4 flex flex-col gap-2">
                <Button type="button" className="min-h-10" onClick={resumeScanning}>
                  {t("views.pantry.scanTryAgain")}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="min-h-10"
                  onClick={() => setPhase("manual")}
                >
                  {t("views.pantry.addManuallyButton")}
                </Button>
              </div>
            </div>
          )}
        </div>

        <button
          type="button"
          className="self-center text-sm text-ink-2 underline"
          onClick={() => setPhase("manual")}
        >
          {t("views.pantry.addManuallyButton")}
        </button>
      </DialogContent>

      {phase === "manual" ? (
        <ManualEntrySheet
          extracting={capture.extracting}
          onSubmit={handleManualSubmit}
          onCancel={() => setPhase("scanning")}
        />
      ) : null}

      {capture.extraction ? (
        <CandidateConfirmSheet
          extraction={capture.extraction}
          confirming={capture.confirming}
          productImageUrl={product?.imageUrl}
          initialExpiryDate={capture.pendingExpiry}
          onConfirm={candidates => void handleConfirm(candidates)}
          onCancel={() => {
            capture.cancelExtraction()
            resumeScanning()
          }}
        />
      ) : null}
    </Dialog>
  )
}
